import { useState } from 'react';
import './Footer.css';

type DialogType = 'disclaimer' | 'privacy' | null;

function DisclaimerDialog({ onClose }: { onClose: () => void }) {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="footer-dialog-backdrop" onClick={handleBackdropClick}>
      <div className="footer-dialog" role="dialog" aria-modal="true" aria-labelledby="disclaimer-title">
        <div className="footer-dialog-header">
          <h2 id="disclaimer-title">Disclaimer</h2>
          <button className="footer-dialog-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="footer-dialog-content">
          <section className="footer-dialog-section">
            <h3>Not Affiliated with Wizards of the Coast</h3>
            <p>
              Mythic Card Forge is an independent, fan-made tool and is not affiliated with,
              endorsed by, or in any way officially connected to Wizards of the Coast LLC or
              Hasbro, Inc.
            </p>
          </section>

          <section className="footer-dialog-section">
            <h3>Trademarks</h3>
            <p>
              Dungeons &amp; Dragons, D&amp;D, and all associated logos, characters, and trade
              dress are trademarks of Wizards of the Coast LLC. All such trademarks are the
              property of their respective owners and are used here without intent to infringe.
            </p>
          </section>

          <section className="footer-dialog-section">
            <h3>Published Content &amp; SRD</h3>
            <p>
              All game content available through this tool is drawn from the{' '}
              <strong>Systems Reference Document (SRD)</strong>, which is made publicly
              available by Wizards of the Coast under the Creative Commons Attribution 4.0
              International License. You can read the full SRD at{' '}
              <a
                href="https://www.dndbeyond.com/srd"
                target="_blank"
                rel="noopener noreferrer"
              >
                dndbeyond.com/srd
              </a>
              .
            </p>
          </section>

          <section className="footer-dialog-section">
            <h3>User-Provided Content</h3>
            <p>
              Mythic Card Forge allows users to import custom content (e.g. via Google Sheets).
              We are not responsible for the accuracy, legality, or appropriateness of any
              user-provided content. Use of custom content is entirely at your own risk.
            </p>
          </section>

          <section className="footer-dialog-section">
            <h3>No Warranty</h3>
            <p>
              This software is provided <strong>"as-is"</strong>, without warranty of any kind,
              express or implied. We make no guarantees regarding availability, accuracy, or
              fitness for any particular purpose. Use at your own risk.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function PrivacyPolicyDialog({ onClose }: { onClose: () => void }) {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="footer-dialog-backdrop" onClick={handleBackdropClick}>
      <div className="footer-dialog" role="dialog" aria-modal="true" aria-labelledby="privacy-title">
        <div className="footer-dialog-header">
          <h2 id="privacy-title">Privacy Policy</h2>
          <button className="footer-dialog-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="footer-dialog-content">
          <section className="footer-dialog-section">
            <h3>Overview</h3>
            <p>
              Mythic Card Forge is a client-side tool. We do not run user accounts, we do not
              store your data on any server, and we do not sell or share personal information
              with third parties. This policy explains the limited data we do collect and why.
            </p>
          </section>

          <section className="footer-dialog-section">
            <h3>Data We Do Not Collect</h3>
            <p>
              We do not collect, store, or transmit any of the following: your name, email
              address, IP address, or any content you import or create within the app. All
              card selections, custom imports, and preferences exist only in your browser
              session and are discarded when you close the tab.
            </p>
          </section>

          <section className="footer-dialog-section">
            <h3>Error Monitoring</h3>
            <p>
              We use an error tracking service to automatically capture unhandled errors and
              exceptions. When an error occurs, it may collect the error message, stack trace,
              browser type, and operating system. No personally identifiable information is
              intentionally included in these reports. Error data is used solely to identify
              and fix bugs.
            </p>
          </section>

          <section className="footer-dialog-section">
            <h3>Anonymized Usage Telemetry</h3>
            <p>
              We collect anonymized, aggregated usage telemetry to understand how the app is
              used and where to focus improvements. This data cannot be traced back to any
              individual user and includes no personally identifiable information.
            </p>
          </section>

          <section className="footer-dialog-section">
            <h3>Third-Party Services</h3>
            <p>
              Beyond Sentry, no third-party analytics or advertising services are used. If you
              choose to import content via Google Sheets, your request goes directly from your
              browser to Google's servers; we never proxy or log that traffic.
            </p>
          </section>

          <section className="footer-dialog-section">
            <h3>Changes to This Policy</h3>
            <p>
              We may update this policy from time to time. Continued use of Mythic Card Forge
              after any changes constitutes acceptance of the revised policy.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export function Footer() {
  const [openDialog, setOpenDialog] = useState<DialogType>(null);
  const currentYear = new Date().getFullYear();

  return (
    <>
      <footer className="site-footer">
        <div className="footer-inner">
          <span className="footer-copyright">
            &copy; {currentYear} Mythic Forge
          </span>
          <span className="footer-sep" aria-hidden="true">·</span>
          <button
            className="footer-link"
            onClick={() => setOpenDialog('disclaimer')}
          >
            Disclaimer
          </button>
          <span className="footer-sep" aria-hidden="true">·</span>
          <button
            className="footer-link"
            onClick={() => setOpenDialog('privacy')}
          >
            Privacy Policy
          </button>
        </div>
      </footer>

      {openDialog === 'disclaimer' && (
        <DisclaimerDialog onClose={() => setOpenDialog(null)} />
      )}
      {openDialog === 'privacy' && (
        <PrivacyPolicyDialog onClose={() => setOpenDialog(null)} />
      )}
    </>
  );
}
